import { useEffect, useRef, useState } from "react";
import type { ReactElement } from "react";
import type { LayoutGrafik, LayoutGrafikAnlegen, LayoutGrafikFelder } from "@kundeninfo/api-client";
import { aktionsfarbenAus, Modal, ModalFuss, useZiehOrdnung } from "@kundeninfo/ui";
import { Auswahl, Eingabe, Hinweis, Optionen, Schalter } from "@kundeninfo/ui/me-design";
import type { ZiehOrdnung } from "@kundeninfo/ui";
import type { PanelProps } from "../LayoutRegister.js";
import { Dateiwahl, useBeimVerlassen } from "../../meHuellen.js";
import { BILD_ACCEPT, istBildDateiname } from "../../../bildAuswahl.js";
import {
  LayEingabe, LayKnopf, SchalterFeld, ZahlFeld, feldId,
} from "./felder.js";
import {
  kopfbereichMm, logoLage, logoMaxProzent, logoProzent, mitKopfVorschlag, mitLogoLage, mitWert,
  ohneLogoLage, wertAn,
} from "../layoutEntwurf.js";
import type { LogoKontext, LogoMasse } from "../layoutEntwurf.js";

/**
 * Register „Marke" (Spec, Oberfläche, rechts). Akzentfarbe und Logo gehören dem
 * UNTERNEHMEN und stehen hier zur Ansicht, mit Sprung dorthin; die Akzentfarbe ist seit dem
 * Nachtrag 20.10.2026 je Layout übersteuerbar (`farben.akzent`, `AkzentAbschnitt`). Die
 * Grafiken sind eigene Einträge am Layout: Jede Änderung ist ein Serverweg (`wege`). Der
 * Entwurf bleibt davon unberührt. Bearbeitet wird INLINE im Register (Vollseiten-Ausnahme,
 * Entscheid 10); Schrift und übrige Palette bleiben Systemvorgabe.
 */

export type GrafikTeil = Partial<Omit<LayoutGrafikFelder, "version">>;

/** Lage und Maß einer Grafik aus dem Blatt (Zug, Griff, Taste), solange ihr Weg läuft;
 *  `heightMm` null = Kasten ohne Höhe. */
export type GrafikZug = { gid: string; xMm: number; yMm: number; widthMm: number;
                          heightMm: number | null };

/** Ein Rasterbild (PNG/JPEG) trägt im PDF keine Deckkraft (Nachtrag L5). */
function istRasterbild(g: LayoutGrafik): boolean {
  return g.bildTyp === "image/png" || g.bildTyp === "image/jpeg";
}

export type GrafikWege = {
  grafikUrl: (gid: string) => string;
  arbeitet: boolean;
  /** Die Engine kennt Belegdesign Etappe 2 (`belegdesign2`): Kastenhöhe und „Funktion
   *  darunter“ am Unterschriftsplatz. Ohne bleiben die Felder verborgen. */
  faehig: boolean;
  /** Die Engine kennt die Unterschrift am Ende des Textes (`unterschriftTextende`, Spec-
   *  Nachtrag 11.10.2026). Ohne bleibt die Auswahl der Platzierung verborgen. */
  textendeFaehig: boolean;
  /** Befunde einer Ablehnung: an der Grafik `gid`; `"neu"` (Hochladen, Unterschriftsplatz)
   *  zeigt das Panel mit der Servermeldung unter „Datei hochladen" (Fixrunde 2, W1). */
  fehler: { gid: string; felder: Record<string, string>; meldung?: string } | null;
  /** Eine Lage aus dem Blatt, deren Serverweg noch läuft — die Felder zeigen sie. */
  ziehen: GrafikZug | null;
  onHochladen: (datei: File) => void;
  onUnterschriftsplatz: () => void;
  onAendern: (g: LayoutGrafik, teil: GrafikTeil) => void;
  onEntfernen: (g: LayoutGrafik) => void;
  onVerschieben: (von: number, nach: number) => void;
  onUnternehmen: () => void;
};

/** Vorgaben einer hochgeladenen Grafik: erste Seite, unten rechts im Satzspiegel. */
export const STEMPEL_VORGABE: Omit<LayoutGrafikAnlegen, "version" | "kind" | "name"> = {
  pages: "FIRST", xMm: 150, yMm: 240, widthMm: 40, opacity: 1, caption: false,
  documentKinds: ["ANGEBOT", "AUFTRAGSBESTAETIGUNG"],
};

/**
 * Das LOGO im Register Marke (Belegdesign Etappe 2, Aufgabe 7): Lage und Größe sind
 * ENTWURFSWERTE (`logo.*`, `kopfbereich.*`) — keine Serverwege. `kontext` trägt Vorschlag,
 * eingepasste Höhe und Grenzen (vom Editor gerechnet, derselbe wie beim Ziehen),
 * `gesperrt` den fremden Stand.
 */
export type LogoWege = {
  faehig: boolean;
  masse: LogoMasse;
  grundbreiteMm: number;
  kontext: LogoKontext;
  gesperrt: boolean;
};

/** Vorgaben des Unterschriftsplatzes: Angebot und Auftragsbestätigung, Name darunter. */
export const UNTERSCHRIFT_VORGABE: Omit<LayoutGrafikAnlegen, "version" | "kind"> = {
  name: "Unterschrift Vertreter", pages: "FIRST", xMm: 120, yMm: 250, widthMm: 50,
  opacity: 1, caption: true, documentKinds: ["ANGEBOT", "AUFTRAGSBESTAETIGUNG"],
};

const SEITEN_TEXT: Record<string, string> = {
  FIRST: "erste Seite", FOLLOWING: "Folgeseiten", ALL: "alle Seiten",
};

/** Die Platzierungen des Unterschriftsplatzes (Entscheid 84) in der Auswahl. */
const PLATZIERUNG_TEXT: Record<LayoutGrafik["placement"], string> = {
  FIXED: "feste Position", TEXT_END: "am Ende des Textes",
};

/** Der Hinweis am Unterschriftsplatz am Textende (statt des Ziehrahmens im Blatt). */
export const TEXTENDE_HINWEIS = "Die Unterschrift folgt auf den letzten Text des Belegs";

/** Ein leeres Feld zeigt die Zahl so, wie der Server sie liefert, sonst leer. */
function mmOderLeer(wert: number | null): string {
  return wert === null ? "" : String(wert);
}

function zahl(text: string): number | null {
  const wert = Number(text.trim().replace(",", "."));
  return text.trim() === "" || !Number.isFinite(wert) ? null : wert;
}

type ZeileProps = {
  g: LayoutGrafik; index: number; anzahl: number; stand: PanelProps["stand"];
  wege: GrafikWege; ordnung: ZiehOrdnung; onEntfernenFragen: (g: LayoutGrafik) => void;
};

function GrafikZeile({ g, index, anzahl, stand, wege, ordnung, onEntfernenFragen }: ZeileProps):
    ReactElement {
  const zug = wege.ziehen?.gid === g.id ? wege.ziehen : null;
  const [name, setName] = useState(g.name);
  const [x, setX] = useState(String(g.xMm));
  const [y, setY] = useState(String(g.yMm));
  const [breite, setBreite] = useState(String(g.widthMm));
  const [hoehe, setHoehe] = useState(g.heightMm === null ? "" : String(g.heightMm));
  const [deckkraft, setDeckkraft] = useState(String(Math.round(g.opacity * 100)));
  const [links, setLinks] = useState(mmOderLeer(g.offsetLeftMm));
  const [oben, setOben] = useState(mmOderLeer(g.offsetTopMm));
  // Der Serverstand gewinnt, sobald er sich ändert (Antwort eines Wegs, Verwerfen).
  useEffect(() => {
    setName(g.name); setX(String(g.xMm)); setY(String(g.yMm));
    setBreite(String(g.widthMm)); setDeckkraft(String(Math.round(g.opacity * 100)));
    setHoehe(g.heightMm === null ? "" : String(g.heightMm));
    setLinks(mmOderLeer(g.offsetLeftMm)); setOben(mmOderLeer(g.offsetTopMm));
  }, [g.name, g.xMm, g.yMm, g.widthMm, g.opacity, g.heightMm, g.offsetLeftMm, g.offsetTopMm]);
  // Am Textende zählen x/y/Seiten nicht (sie bleiben gespeichert, ein Rückwechsel hat die
  // alte Lage) — die Felder sind dann ausgeblendet.
  const amTextende = g.kind === "SIGNATURE" && g.placement === "TEXT_END";
  // Die Auswahl nur mit Fähigkeit der Engine — oder wenn der Platz schon am Textende steht
  // (sonst käme man ohne Fähigkeit nicht mehr zurück auf „fest“).
  const zeigePlatzierung = g.kind === "SIGNATURE" && (wege.textendeFaehig || amTextende);

  const fehler = wege.fehler?.gid === g.id ? wege.fehler.felder : {};
  const fehlerId = (feld: string): string => `lay-grafik-${g.id}-${feld}-fehler`;
  // Während eines Wegs sind Eingaben `readOnly` + `aria-disabled`, nicht `disabled`: Ein
  // gesperrtes Feld verlöre den Tastaturfokus an `body` (Fixrunde 2, W2). Auswahl und
  // Schalter verwerfen Änderungen; ein Verlassen-Weg geht in die Laufsperre des Editors.
  // Ein nach einer Ablehnung stehender Wert springt beim nächsten Serverstand derselben
  // Grafik auf diesen zurück (Effekt oben) — gewollt, der Serverstand gewinnt. Im Fremden
  // Stand sperrt die `fieldset` des Editors Felder und Knöpfe (me-design 6bfec97).
  const gesperrt = wege.arbeitet;
  const faehig = wege.faehig;
  // Welche Felder diese Zeile zeigt — ein Befund an einem gezeigten Feld steht im Feld
  // (me-eingabe/me-auswahl/me-schalter `fehler`), alle übrigen sichtbar unter der Zeile.
  const gezeigt = new Set<string>(["name", "widthMm"]);
  if (!amTextende) { gezeigt.add("pages"); gezeigt.add("xMm"); gezeigt.add("yMm"); }
  if (amTextende) { gezeigt.add("offsetLeftMm"); gezeigt.add("offsetTopMm"); }
  if (zeigePlatzierung) gezeigt.add("placement");
  if (g.kind === "SIGNATURE" && faehig) { gezeigt.add("heightMm"); gezeigt.add("captionJobTitle"); }
  if (g.kind === "SIGNATURE") gezeigt.add("caption");
  if (!istRasterbild(g)) gezeigt.add("opacity");

  /** Eine Zahl beim Verlassen: nur Gültiges und Geändertes geht hinaus; Ungültiges
   *  springt auf den Serverstand zurück. */
  function zahlVerlassen(text: string, alt: number, zuruecksetzen: () => void,
                         teil: (n: number) => GrafikTeil): void {
    const n = zahl(text);
    if (n === null) { zuruecksetzen(); return; }
    if (n !== alt) wege.onAendern(g, teil(n));
  }

  /** Ein mm-Feld der Grafik: sichtbare Beschriftung als `<label for>`, Name „<Grafik>:
   *  <Beschriftung>“ als eigenes aria-label (bleibt seit me-design ba10760 Name, P3-5), die
   *  Einheit im Feld (`einheit`, Entscheid 39). */
  function mmEingabe(feld: "xMm" | "yMm" | "widthMm" | "heightMm" | "offsetLeftMm" | "offsetTopMm"
                       | "opacity", label: string, einheit: string, text: string,
                     setzen: (t: string) => void, verlassen: () => void,
                     platzhalter?: string): ReactElement {
    const id = `lay-grafik-${g.id}-${feld}`;
    return (
      <div className="lay__feld lay__feld--klein">
        <label className="lay__label" htmlFor={id}>{label}</label>
        <div className="lay__eingabezeile">
          <LayEingabe id={id} name={`${g.name}: ${label}`} zahl schmal wert={text} einheit={einheit}
            platzhalter={platzhalter} gesperrt={gesperrt} fehler={fehler[feld]}
            onText={setzen} onVerlassen={verlassen} />
        </div>
      </div>
    );
  }

  function mmFeld(feld: "xMm" | "yMm" | "widthMm", label: string, text: string,
                  setzen: (t: string) => void): ReactElement {
    const wert = zug !== null ? String(zug[feld]) : text;
    return mmEingabe(feld, label, "mm", wert, setzen,
      () => zahlVerlassen(text, g[feld], () => setzen(String(g[feld])),
        (n) => (feld === "xMm" ? { xMm: n } : feld === "yMm" ? { yMm: n } : { widthMm: n })));
  }

  /** Ein Abstand am Textende (Entscheid 85): leer = Vorgabe der Vorlage (Platzhalter) —
   *  Leeren schickt `null`, fehlend hiesse beim Server „unverändert“. */
  function abstandFeld(feld: "offsetLeftMm" | "offsetTopMm", label: string, text: string,
                       setzen: (t: string) => void, platzhalter: number): ReactElement {
    const alt = g[feld];
    return mmEingabe(feld, label, "mm", text, setzen, () => {
      if (text.trim() === "") {
        if (alt !== null) wege.onAendern(g, feld === "offsetLeftMm"
          ? { offsetLeftMm: null } : { offsetTopMm: null });
        return;
      }
      zahlVerlassen(text, alt ?? Number.NaN, () => setzen(mmOderLeer(alt)),
        (n) => (feld === "offsetLeftMm" ? { offsetLeftMm: n } : { offsetTopMm: n }));
    }, String(platzhalter).replace(".", ","));
  }

  /** Ein Ja/Nein der Grafik (`me-schalter`). */
  function schalter(feld: "caption" | "captionJobTitle", label: string): ReactElement {
    const text = fehler[feld];
    return (
      <Schalter id={`lay-grafik-${g.id}-${feld}`} beschriftung={label}
        aria-label={`${g.name}: ${label}`} an={g[feld]} className="lay__me"
        {...(text === undefined ? {} : { fehler: text })}
        {...(gesperrt ? { gesperrt: true } : {})}
        onGeaendert={(d) => {
          if (!gesperrt) wege.onAendern(g, feld === "caption" ? { caption: d.an } : { captionJobTitle: d.an });
        }} />
    );
  }

  return (
    <li className="lay__grafik" ref={ordnung.merke(index)} {...ordnung.zeile(index)}>
      <div className="lay__grafikkopf">
        <span className="lay__griff" {...ordnung.griff(index)}>⠿</span>
        {g.kind === "STATIC"
          ? <img className="lay__daumen" src={wege.grafikUrl(g.id)} alt="" />
          : <span className="lay__daumen lay__daumen--unterschrift" aria-hidden="true">✍</span>}
        <LayEingabe id={`lay-grafik-${g.id}-name`} name={`${g.name}: Name`}
          className="lay__grafikname" wert={name} gesperrt={gesperrt} fehler={fehler["name"]}
          onText={setName}
          onVerlassen={() => { if (name.trim() !== g.name) wege.onAendern(g, { name: name.trim() }); }} />
        <LayKnopf art="leise" className="lay__pfeil" deaktiviert={gesperrt || index === 0}
          name={`${g.name} nach oben`}
          onClick={() => wege.onVerschieben(index, index - 1)}>▲</LayKnopf>
        <LayKnopf art="leise" className="lay__pfeil" deaktiviert={gesperrt || index === anzahl - 1}
          name={`${g.name} nach unten`}
          onClick={() => wege.onVerschieben(index, index + 1)}>▼</LayKnopf>
        <LayKnopf art="leise" deaktiviert={gesperrt} name={`${g.name} entfernen`}
          onClick={() => onEntfernenFragen(g)}>Entfernen</LayKnopf>
      </div>
      {g.kind === "SIGNATURE" && (
        <p className="lay__panelhinweis">Unterschrift des Vertreters am Beleg — im
          Muster: Beispielunterschrift.</p>
      )}
      {zeigePlatzierung && (
        <div className="lay__grafikfelder">
          <div className="lay__feld">
            <Auswahl id={`lay-grafik-${g.id}-placement`} beschriftung="Platzierung"
              aria-label={`${g.name}: Platzierung`} className="lay__me" wert={g.placement}
              {...(fehler["placement"] === undefined ? {} : { fehler: fehler["placement"] })}
              {...(gesperrt ? { gesperrt: true } : {})}
              onGeaendert={(d) => {
                if (!gesperrt) wege.onAendern(g, { placement: d.wert as LayoutGrafik["placement"] });
              }}>
              {(["FIXED", "TEXT_END"] as const).map((pl) =>
                <option key={pl} value={pl}>{PLATZIERUNG_TEXT[pl]}</option>)}
            </Auswahl>
          </div>
        </div>
      )}
      {g.kind === "SIGNATURE" && !zeigePlatzierung && wege.faehig && (
        <p className="lay__panelhinweis">Platzierung „am Ende des Textes“: steht bereit, sobald
          die Beleg-Engine aktualisiert ist.</p>
      )}
      {amTextende && (
        <p className="lay__panelhinweis">{TEXTENDE_HINWEIS} — die Vorschau zeigt die echte
          Lage.</p>
      )}
      <div className="lay__grafikfelder">
        {!amTextende && (
        <div className="lay__feld lay__feld--klein">
          <Auswahl id={`lay-grafik-${g.id}-pages`} beschriftung="Seiten"
            aria-label={`${g.name}: Seiten`} className="lay__me" wert={g.pages}
            {...(fehler["pages"] === undefined ? {} : { fehler: fehler["pages"] })}
            {...(gesperrt ? { gesperrt: true } : {})}
            onGeaendert={(d) => { if (!gesperrt) wege.onAendern(g, { pages: d.wert }); }}>
            {stand.katalog.grafikSeiten.map((s) =>
              <option key={s} value={s}>{SEITEN_TEXT[s] ?? s}</option>)}
          </Auswahl>
        </div>
        )}
        {!amTextende && mmFeld("xMm", "x", x, setX)}
        {!amTextende && mmFeld("yMm", "y", y, setY)}
        {amTextende && abstandFeld("offsetLeftMm", "Abstand vom linken Rand", links, setLinks,
          stand.katalog.unterschriftTextende.abstandLinksMm)}
        {amTextende && abstandFeld("offsetTopMm", "Abstand zum Text darüber", oben, setOben,
          stand.katalog.unterschriftTextende.abstandObenMm)}
        {mmFeld("widthMm", "Breite", breite, setBreite)}
        {/* Höhe leer = wie bisher (Kasten ohne Höhe); Entfernen schickt `heightMm: null`
            — fehlend hiesse beim Server „unverändert“ (Bericht A5). */}
        {g.kind === "SIGNATURE" && faehig && mmEingabe("heightMm", "Höhe", "mm",
          zug?.heightMm != null ? String(zug.heightMm) : hoehe, setHoehe,
          () => {
            if (hoehe.trim() === "") {
              if (g.heightMm !== null) wege.onAendern(g, { heightMm: null });
              return;
            }
            zahlVerlassen(hoehe, g.heightMm ?? Number.NaN,
              () => setHoehe(g.heightMm === null ? "" : String(g.heightMm)),
              (n) => ({ heightMm: n }));
          }, "wie bisher")}
        {istRasterbild(g) ? (
          <div className="lay__feld lay__feld--klein">
            <span className="lay__label">Deckkraft</span>
            <span className="lay__panelhinweis">Deckkraft nur bei SVG</span>
          </div>
        ) : mmEingabe("opacity", "Deckkraft", "%", deckkraft, setDeckkraft,
          () => zahlVerlassen(deckkraft, Math.round(g.opacity * 100),
            () => setDeckkraft(String(Math.round(g.opacity * 100))),
            (n) => ({ opacity: n / 100 })))}
      </div>
      {g.kind === "SIGNATURE" && (
        <div className="lay__grafikfelder">
          {schalter("caption", "Name darunter")}
          {faehig && schalter("captionJobTitle", "Funktion darunter")}
          {/* Belegarten als `Optionen mehrfach` (me-optionen, Entscheid 40, ME-Etappe 4
              P17; vorher native Kästchen, Übergang P4-3). Ohne sichtbare Überschrift wie
              bisher; der Name der Gruppe nennt die Grafik. Die Werte sind Enum-Namen (ohne
              Komma). Gesperrt während eines Wegs: Fokus bleibt, nichts änderbar. */}
          <Optionen mehrfach richtung="waagerecht" aria-label={`${g.name}: Belegarten`}
            wert={g.documentKinds}
            {...(gesperrt ? { gesperrt: true } : {})}
            onGeaendert={(d) => {
              if (gesperrt) return;
              const gewaehlt = new Set(d.werte ?? []);
              // In Katalogreihenfolge — so vergleicht sich der Stand stabil (R-VERGLEICH).
              wege.onAendern(g, { documentKinds: stand.katalog.belegarten
                .map((a) => a.art).filter((a) => gewaehlt.has(a)) });
            }}>
            {stand.katalog.belegarten.map((b) => (
              <option key={b.art} value={b.art}>{b.label}</option>
            ))}
          </Optionen>
        </div>
      )}
      {/* Ein Befund an einem gezeigten Feld steht (und wird angesagt) im Feld selbst
          (`aria-live` am Fehlertext, me-design ba10760); die übrigen sichtbar hier. */}
      {Object.entries(fehler).filter(([feld]) => !gezeigt.has(feld)).map(([feld, text]) => (
        <span key={feld} id={fehlerId(feld)} className="lay__feldfehler" role="alert">{text}</span>
      ))}
    </li>
  );
}

/** Der Hinweis am Farbfeld der abweichenden Akzentfarbe (Nachtrag 20.10.2026). */
export const AKZENT_HINWEIS = "Für den Druck eine dunklere Farbe als die Bildschirmfarbe wählen";

const HEX_FARBE = /^#[0-9A-Fa-f]{6}$/;

/**
 * Die ABWEICHENDE AKZENTFARBE des Belegs (Nachtrag 20.10.2026, Dirk): Die Farbe des
 * Unternehmens ist für den Druck oft zu hell. Der Schalter setzt `farben.akzent` im Entwurf
 * (die Engine nimmt sie vor der Unternehmensfarbe, R-AKZENT-WIRKUNG); aus entfernt den
 * Schlüssel, dann gilt wieder das Unternehmen. Beim Einschalten steht der abgeleitete
 * dunklere Ton (dieselbe Rechnung wie der Aktionston der Oberfläche) als Vorschlag im Feld.
 * Das Farbfeld ist `me-eingabe art="farbe"`; die Palette bietet als erste Reihe die
 * Unternehmensfarbe und den Vorschlag. Getippte Zwischenstände gehen so hinaus — die
 * Whitelist sagt am Feld, was fehlt. Gespeichert wird mit dem Entwurf (keine eigene Frage).
 */
function AkzentAbschnitt(p: PanelProps): ReactElement {
  const { entwurf, feldFehler, stand } = p;
  const pfad = ["farben", "akzent"] as const;
  const wert = wertAn(entwurf, pfad);
  const an = wert !== undefined;
  const unternehmen = stand.unternehmen.colorHex;
  const vorschlag = HEX_FARBE.test(unternehmen) ? aktionsfarbenAus(unternehmen).aktion : null;
  const [text, setText] = useState(typeof wert === "string" ? wert : "");
  // Kommt der Wert von aussen (Verwerfen, Übernehmen, Schalter), folgt das Feld.
  useEffect(() => {
    setText((alt) => (alt === wert ? alt : typeof wert === "string" ? wert : ""));
  }, [wert]);
  const host = useRef<HTMLElement | null>(null);
  useBeimVerlassen(host, p.onFeldVerlassen);
  const id = feldId("farben.akzent");
  const fehler = feldFehler["farben.akzent"] ?? feldFehler.farben;
  const palette = [{ hex: unternehmen, name: "Farbe des Unternehmens" },
    ...(vorschlag !== null && vorschlag.toUpperCase() !== unternehmen.toUpperCase()
      ? [{ hex: vorschlag, name: "Dunkler (Vorschlag für den Druck)" }] : [])]
    .filter((f) => HEX_FARBE.test(f.hex));
  return (
    <>
      {/* Ohne „Systemwert“-Knopf: Aus IST der Systemwert (die Farbe des Unternehmens). */}
      <div className="lay__feld">
        <Schalter id={feldId("farben.akzentAn")} beschriftung="Abweichende Akzentfarbe für den Beleg"
          an={an} className={`lay__me${an ? " lay__eigen" : ""}`}
          onGeaendert={(d) => {
            p.onEntwurf(mitWert(entwurf, pfad, d.an
              ? (vorschlag ?? (HEX_FARBE.test(unternehmen) ? unternehmen.toUpperCase() : "#000000"))
              : undefined));
            p.onFeldVerlassen();
          }} />
      </div>
      {an && (
        <div className="lay__feld">
          <Eingabe ref={host} id={id} className="lay__me lay__eigen lay__akzentfarbe" art="farbe"
            beschriftung="Akzentfarbe des Belegs" wert={text} hilfe={AKZENT_HINWEIS}
            {...(fehler === undefined ? {} : { fehler })}
            onGeaendert={(d) => {
              setText(d.wert);
              p.onEntwurf(mitWert(entwurf, pfad, d.wert.trim()));
              // Eine fertige Farbe (Palette, Systemwähler, sechs Stellen) rendert gleich neu.
              if (HEX_FARBE.test(d.wert.trim())) p.onFeldVerlassen();
            }}>
            {palette.map((f) => <option key={f.hex} value={f.hex}>{f.name}</option>)}
          </Eingabe>
        </div>
      )}
    </>
  );
}

/** Der Wortlaut ohne Fähigkeit der Engine (Brief A7, Schritt 1). */
export const OHNE_FAEHIGKEIT = "Die Beleg-Engine kennt die neuen Einstellungen noch nicht";

function mmText(mm: number): string {
  return String(Math.round(mm * 10) / 10).replace(".", ",");
}

/**
 * Abschnitt LOGO (Entscheide 65 a, 68 a, 69; Brief A7, Schritt 2): Größe in %, x und y in mm,
 * „Logo nur auf Seite 1“, „Wie bisher“. Das erste Setzen von x oder y nimmt für den anderen
 * Wert den Vorschlag und schlägt den Pflicht-Kopfbereich vor (`mitLogoLage`). Befunde am Feld
 * aus der Whitelist (`feldFehler`); die Kopplung mit dem echten Logo prüft der Server beim
 * Speichern (Befund am Kopfbereich, hier unter dem Abschnitt).
 */
function LogoAbschnitt(p: PanelProps & { logo: LogoWege }): ReactElement {
  const { entwurf, logo, feldFehler } = p;
  // Bedienbar nur mit einem Logo, mit dem Belege RENDERN (Maße gelesen, Review A7 H2) —
  // `logoVorhanden` zählt auch eine reine Dunkel-Variante, die kein Beleg zeigt.
  const vorhanden = logo.masse !== null;
  const gesperrt = logo.gesperrt || !vorhanden;
  const lage = logoLage(entwurf);
  const prozentWert = wertAn(entwurf, ["logo", "prozent"]);
  const maxProzent = logoMaxProzent(logo.masse, logo.grundbreiteMm);
  const prozent = logoProzent(entwurf);
  const warnung = maxProzent !== null && prozent > 100 && prozent > maxProzent
    ? `größer nicht möglich, Bildauflösung zu gering (max ${Math.floor(maxProzent)} %)`
    : undefined;
  const nurSeite1 = wertAn(entwurf, ["logo", "nurSeite1"]) === true;
  const erste = kopfbereichMm(entwurf, "ersteSeiteMm");
  const folge = kopfbereichMm(entwurf, "folgeseitenMm");
  const eigen = wertAn(entwurf, ["logo", "xMm"]) !== undefined
    || wertAn(entwurf, ["logo", "yMm"]) !== undefined || prozentWert !== undefined;

  function lageWert(feld: "xMm" | "yMm"): unknown {
    return wertAn(entwurf, ["logo", feld]);
  }
  function lageSetzen(feld: "xMm" | "yMm", neu: number | string | undefined): void {
    if (typeof neu === "number") {
      // Ohne Kopfbereich-Vorschlag beim Tippen — der kommt beim Verlassen (Fixrunde W1).
      p.onEntwurf(mitLogoLage(entwurf, feld === "xMm" ? { xMm: neu } : { yMm: neu },
                              logo.kontext, false));
    } else {
      // Leer oder keine Zahl: so hinaus — die Whitelist sagt am Feld, was fehlt.
      p.onEntwurf(mitWert(entwurf, ["logo", feld], neu));
    }
  }
  /** Beim Verlassen von x/y: erst jetzt den fehlenden Pflicht-Kopfbereich aus der fertig
   *  getippten Lage vorschlagen, dann die Vorschau. */
  function lageVerlassen(): void {
    const neu = mitKopfVorschlag(entwurf, logo.kontext);
    if (neu !== entwurf) p.onEntwurf(neu);
    p.onFeldVerlassen();
  }
  const befundeKopf = (["kopfbereich.ersteSeiteMm", "kopfbereich.folgeseitenMm", "logo"] as const)
    .filter((pfad) => feldFehler[pfad] !== undefined);

  return (
    <fieldset className="lay__gruppe">
      <legend>Logo</legend>
      {!vorhanden && (
        <p className="lay__hinweis">{p.stand.unternehmen.logoVorhanden
          ? "Kein Logo für Belege hinterlegt (nur eine Variante, die Belege nicht verwenden)"
          : "Kein Logo hinterlegt"}</p>
      )}
      <p className="lay__panelhinweis">Größe in Prozent der heutigen Logobreite
        ({mmText(logo.grundbreiteMm)} mm); Lage in Millimetern ab der linken oberen Blattkante,
        im Blatt lässt sich der Rahmen ziehen. Das Logo bleibt im Kopfbereich.</p>
      <div className="lay__grafikfelder">
        <ZahlFeld id={feldId("logo.prozent")} label="Größe" einheit="%" wert={prozentWert}
          platzhalter="100" fehler={feldFehler["logo.prozent"]} hinweis={warnung}
          gesperrt={gesperrt} systemwertKnopf
          onWert={(neu) => p.onEntwurf(mitWert(entwurf, ["logo", "prozent"], neu))}
          onVerlassen={p.onFeldVerlassen} />
        <ZahlFeld id={feldId("logo.xMm")} label="x" einheit="mm" wert={lageWert("xMm")}
          platzhalter={lage === null ? "Textfluss" : ""} fehler={feldFehler["logo.xMm"]}
          gesperrt={gesperrt} onWert={(neu) => lageSetzen("xMm", neu)}
          onVerlassen={lageVerlassen} />
        <ZahlFeld id={feldId("logo.yMm")} label="y" einheit="mm" wert={lageWert("yMm")}
          platzhalter={lage === null ? "Textfluss" : ""} fehler={feldFehler["logo.yMm"]}
          gesperrt={gesperrt} onWert={(neu) => lageSetzen("yMm", neu)}
          onVerlassen={lageVerlassen} />
      </div>
      {/* Beim fremden Stand sperrt die `fieldset` des Editors den Schalter. */}
      {vorhanden && (
        <SchalterFeld id={feldId("logo.nurSeite1")} label="Logo nur auf Seite 1"
          wert={wertAn(entwurf, ["logo", "nurSeite1"])} systemwert={false}
          fehler={feldFehler["logo.nurSeite1"]}
          onWert={(neu) => {
            // Ohne „nur Seite 1“ braucht das freie Logo auch den Kopfbereich der
            // Folgeseiten (Pflicht) — der Vorschlag füllt ihn, falls er fehlt.
            p.onEntwurf(mitKopfVorschlag(mitWert(entwurf, ["logo", "nurSeite1"], neu),
                                         logo.kontext));
            p.onFeldVerlassen();
          }} />
      )}
      <div className="lay__werkzeuge">
        <LayKnopf deaktiviert={gesperrt || !eigen}
          onClick={() => { p.onEntwurf(ohneLogoLage(entwurf)); p.onFeldVerlassen(); }}>
          Wie bisher</LayKnopf>
      </div>
      <p className="lay__panelhinweis">
        {erste === null && folge === null
          ? "Kopfbereich: wie die Seitenränder — beim ersten Setzen der Lage schlägt der Editor ihn vor."
          : `Kopfbereich bis ${erste === null ? "—" : `${mmText(erste)} mm`} (Seite 1)`
            + (nurSeite1 ? "" : `, ${folge === null ? "—" : `${mmText(folge)} mm`} (Folgeseiten)`)
            + " ab der oberen Blattkante."}
      </p>
      {befundeKopf.map((pfad) => (
        <span key={pfad} id={`${feldId(pfad)}-fehler`} className="lay__feldfehler"
          role="alert">{feldFehler[pfad]}</span>
      ))}
    </fieldset>
  );
}

export function MarkePanel(p: PanelProps & { wege: GrafikWege; logo: LogoWege }): ReactElement {
  const { stand, wege } = p;
  const [entfernen, setEntfernen] = useState<LayoutGrafik | null>(null);
  const [dateiFehler, setDateiFehler] = useState<string | null>(null);
  const grafiken = stand.grafiken;
  const ordnung = useZiehOrdnung(grafiken.length, (von, nach) => {
    if (!wege.arbeitet) wege.onVerschieben(von, nach);
  });
  const voll = grafiken.length >= 10;
  const hatUnterschrift = grafiken.some((g) => g.kind === "SIGNATURE");

  return (
    <div className="lay__panel">
      <fieldset className="lay__gruppe">
        <legend>Aus dem Unternehmen</legend>
        <div className="lay__eingabezeile">
          <span className="lay__farbfeld" style={{ background: stand.unternehmen.colorHex }}
            aria-hidden="true" />
          <span>Akzentfarbe des Unternehmens (aus den Stammdaten):{" "}
            <code>{stand.unternehmen.colorHex}</code></span>
        </div>
        <AkzentAbschnitt {...p} />
        <p>{stand.unternehmen.logoVorhanden ? "Logo hinterlegt"
          : "Kein Logo hinterlegt — der Beleg zeigt den Namen des Unternehmens."}</p>
        <div className="lay__werkzeuge">
          <LayKnopf onClick={wege.onUnternehmen}>Im Unternehmen pflegen</LayKnopf>
        </div>
      </fieldset>

      {p.logo.faehig
        ? <LogoAbschnitt {...p} />
        : <p className="lay__hinweis">{OHNE_FAEHIGKEIT} — Logo-Lage und -Größe, Kastenhöhe und
            Funktion am Unterschriftsplatz stehen bereit, sobald sie aktualisiert ist.</p>}

      <fieldset className="lay__gruppe">
        <legend>Grafiken ({grafiken.length} von 10)</legend>
        {dateiFehler !== null && (
          <Hinweis art="fehler" className="lay__meldung" aria-label="Dateifehler">{dateiFehler}</Hinweis>
        )}
        <div className="lay__werkzeuge">
          {/* Dateiauswahl `me-eingabe art="datei"` (Entscheid 31); gesperrt statt abgeschaltet
              (der Fokus bleibt), nach jeder Wahl geleert. */}
          <Dateiwahl name="Datei hochladen (SVG, PNG oder JPEG)" accept={BILD_ACCEPT}
            className="lay__dateiwahl" gesperrt={voll || wege.arbeitet} nachWahlLeeren
            onDateien={(dateien) => {
              const datei = dateien[0];
              if (datei === undefined) return;
              // Nur .svg/.png/.jpg/.jpeg — den Inhalt prüft der Server an den Bytes
              // (GRAFIK_UNGUELTIG, GRAFIK_ZU_GROSS, Entscheid 25).
              if (!istBildDateiname(datei.name)) {
                setDateiFehler("Nur SVG, PNG oder JPEG (.svg, .png, .jpg) sind zulässig.");
                return;
              }
              setDateiFehler(null);
              wege.onHochladen(datei);
            }} />
          <LayKnopf deaktiviert={voll || hatUnterschrift || wege.arbeitet}
            onClick={wege.onUnterschriftsplatz}>Unterschriftsplatz hinzufügen</LayKnopf>
        </div>
        {wege.fehler?.gid === "neu" && (
          <div className="lay__neufehler">
            {wege.fehler.meldung !== undefined && (
              <Hinweis art="fehler" className="lay__meldung" aria-label="Fehler beim Anlegen">
                {wege.fehler.meldung}</Hinweis>
            )}
            {Object.keys(wege.fehler.felder).length > 0 && (
              <ul className="lay__feldfehler">
                {Object.entries(wege.fehler.felder).map(([feld, text]) =>
                  <li key={feld}>{text}</li>)}
              </ul>
            )}
          </div>
        )}
        <p className="lay__panelhinweis">Lage in Millimetern ab der linken oberen Blattkante;
          im Blatt lassen sich die Rahmen ziehen. Grafiken liegen unter dem Belegtext. SVG,
          PNG oder JPEG (höchstens 1 MB und 2000 × 2000 Pixel); die Deckkraft wirkt nur bei
          SVG.</p>
        {grafiken.length === 0
          ? <p className="lay__hinweis">Noch keine Grafiken.</p>
          : (
            <ol className="lay__grafiken">
              {grafiken.map((g, i) => (
                <GrafikZeile key={g.id} g={g} index={i} anzahl={grafiken.length} stand={stand}
                  wege={wege} ordnung={ordnung} onEntfernenFragen={setEntfernen} />
              ))}
            </ol>
          )}
      </fieldset>

      {entfernen !== null && (
        // Gefahr-Frage (Entscheid 32): die Grafik ist danach fort — Fokus auf „Abbrechen“.
        <Modal titel="Grafik entfernen" gestapelt gefahr onAbbrechen={() => setEntfernen(null)}>
          <p>„{entfernen.name}“ wird aus dem Entwurf entfernt. Belege ändern sich erst mit
            der nächsten Freigabe.</p>
          <ModalFuss speichernText="Entfernen" gefahr onAbbrechen={() => setEntfernen(null)}
            onSpeichern={() => { wege.onEntfernen(entfernen); setEntfernen(null); }} />
        </Modal>
      )}
    </div>
  );
}
